<template>
  <div class="!border-t-0 relative w-full">
    <div
      class="sticky top-0 z-[2] -mb-4 overflow-hidden duration-300"
      :class="{
        'max-h-0 ease-out pb-0': !expanded,
        'max-h-[100px] ease-in delay-300 pb-4': expanded,
      }"
    >
      <div
        class="absolute top-0 left-0 w-full h-full bg-[#ff9239] !shadow-none rounded-t-3xl !border-none flex flex-col"
      >
        <div class="h-5 rounded-t-3xl bg-white w-full mt-auto"></div>
      </div>
      <!-- style="
          mask-image: linear-gradient(to bottom, white, white 70%, transparent);
        " -->
      <div
        class="flex justify-around gap-1 p-4 md:justify-start w-full relative rounded-t-3xl"
      >
        <div
          class="rounded-full shimmerTarget"
          v-for="tab in [
            {
              icon: '/images/icons/motorbike.svg',
              id: 'orders',
              count: 10,
              label: 'Courses',
            },
            {
              icon: '/images/icons/package-delivered.svg',
              id: 'ordersDone',
              count: 10,
              label: 'Livraisons',
            },
            {
              icon: '/images/icons/pie-chart.svg',
              id: 'commissions',
              count: 10,
              label: 'Commissions',
            },
          ]"
          :key="tab.id"
        >
          <button
            @click="selectedTab = tab.id"
            class="buttonComponent py-2 px-5 transition flex gap-1 justify-center items-center"
            :class="{
              'border bg-transparent text-white border-white':
                tab.id !== selectedTab,
              'bg-white text-primary': tab.id === selectedTab,
            }"
          >
            <div>
              <ColorImg :src="tab.icon" class="bg-current w-6 h-6" />
            </div>
            <span class="">{{ tab.label }} </span>
          </button>
        </div>
      </div>
    </div>
    <div
      class="overflow-hidden duration-300"
      :class="{
        'max-h-0 ease-out': !expanded,
        'max-h-[10000px] ease-in delay-300': expanded,
      }"
    >
      <div class="pb-0">
        <DataFetcher dataKey="kyc" @fetched="onKycFetched">
          <template #any="{ loading, data, loadingError }">
            <div class="bg-white pb-12 p-5">
              <!-- KYC Check Overlay -->
              <div v-if="!kycValidated" class="shimmerTarget">
                <!-- Not filled -->
                <div
                  v-if="!loading && kycStatus === 'not_filled'"
                  class="text-center py-10"
                >
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">
                    KYC requis
                  </h3>
                  <p class="text-gray-600 mb-6">
                    Veuillez remplir votre formulaire KYC pour accéder aux
                    livraisons et commissions
                  </p>
                  <router-link to="/kyc" class="buttonComponent">
                    Remplir mon KYC
                  </router-link>
                </div>

                <!-- Pending validation -->
                <div
                  v-else-if="!loading && kycStatus === 'pending'"
                  class="text-center py-10"
                >
                  <h3 class="text-xl font-semibold text-yellow-600 mb-2">
                    KYC en attente de validation
                  </h3>
                  <p class="text-gray-600">
                    Votre KYC est en cours de validation. Vous pourrez accéder
                    aux livraisons une fois validé.
                  </p>
                </div>

                <!-- Rejected -->
                <div
                  v-else-if="!loading && kycStatus === 'rejected'"
                  class="text-center py-10"
                >
                  <h3 class="text-xl font-semibold text-red-600 mb-2">
                    KYC rejeté
                  </h3>
                  <p class="text-gray-600 mb-2">
                    Votre KYC a été rejeté. Veuillez le remplir à nouveau avec
                    des informations correctes.
                  </p>
                  <p
                    v-if="kycStatusData?.rejectionReason"
                    class="text-sm text-red-500 mb-6"
                  >
                    Raison: {{ kycStatusData.rejectionReason }}
                  </p>
                  <router-link to="/kyc" class="buttonComponent">
                    Remplir à nouveau
                  </router-link>
                </div>

                <!-- Loading/Error -->
                <div
                  v-else-if="loading"
                  class="text-center py-10 text-gray-500"
                >
                  <p>Vérification du statut KYC...</p>
                </div>

                <div v-else class="text-center py-10 text-red-500">
                  <p>Impossible de vérifier le statut KYC</p>
                </div>
              </div>

              <!-- Normal list view (only when KYC validated) -->
              <div v-else class="shimmerTarget">
                <!-- Compte Suspendu -->
                <div v-if="isSuspended" class="text-center py-10 px-4">
                  <div class="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
                    <ColorImg src="/images/icons/alerte.svg" class="w-8 h-8 bg-red-600" />
                  </div>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">
                    Compte suspendu
                  </h3>
                  <p class="text-gray-600 mb-4 max-w-sm mx-auto">
                    Votre compte est actuellement suspendu par l'administration. Vous ne pouvez pas recevoir ni accepter de courses.
                  </p>
                </div>

                <!-- Hors ligne (Availability check) -->
                <div v-else-if="!isAvailable" class="text-center py-10 px-4">
                  <div class="w-16 h-16 rounded-full bg-orange-100 text-primary flex items-center justify-center mx-auto mb-3">
                    <ColorImg src="/images/icons/motorbike.svg" class="w-8 h-8 bg-primary" />
                  </div>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">
                    Vous êtes hors ligne
                  </h3>
                  <p class="text-gray-600 mb-6 max-w-sm mx-auto">
                    Activez votre disponibilité pour voir les courses et commencer à recevoir des livraisons.
                  </p>
                  <router-link to="/profile" class="buttonComponent inline-block px-8 py-3 rounded-full font-bold">
                    Passer en ligne
                  </router-link>
                </div>

                <!-- En ligne : Liste normale des commandes -->
                <div v-else>
                <!-- Orders List -->
                <ListFetcher
                  :limit="500"
                  :refreshDelay="10000"
                  :dataKey="
                    selectedTab == 'commissions' ? 'ordersDone' : selectedTab
                  "
                  :key="selectedTab + '-orders'"
                  @fetched="
                    ({ items }) => {
                      ordersEmpty = !items.length
                      ordersList = items
                    }
                  "
                  @dataChanged="
                    ({ old, new: newItems }) => {
                      if (old?.length === newItems?.length) return
                      $emit('dataGot')
                    }
                  "
                >
                  <template #item="{ item, isFirst, isLast }">
                    <div></div>
                  </template>
                  <template #pagination> <div></div> </template>
                  <template #empty> <div></div> </template>
                  <template #error>
                    <div>Erreur de récupération des commandes</div>
                  </template>
                </ListFetcher>
                <!-- Packages List -->
                <ListFetcher
                  :limit="500"
                  :refreshDelay="10000"
                  :dataKey="
                    selectedTab == 'commissions'
                      ? 'packagesDone'
                      : selectedTab == 'ordersDone'
                        ? 'packagesDone'
                        : 'packages'
                  "
                  :key="selectedTab + '-packages'"
                  @fetched="
                    ({ items }) => {
                      packagesEmpty = !items.length
                      packagesList = items
                    }
                  "
                  @dataChanged="
                    ({ old, new: newItems }) => {
                      if (old?.length === newItems?.length) return
                      $emit('dataGot')
                    }
                  "
                >
                  <template #item="{ item, isFirst, isLast }">
                    <div></div>
                  </template>
                  <template #pagination> <div></div> </template>
                  <template #empty> <div></div> </template>
                  <template #error>
                    <div>Erreur des récupération des colis</div>
                  </template>
                </ListFetcher>
                <div
                  v-if="packagesEmpty && ordersEmpty"
                  class="flex flex-col items-center justify-center gap-4 p-8 text-center"
                >
                  <!-- Icône animée -->
                  <div
                    class="relative w-20 h-20 flex items-center justify-center"
                  >
                    <div
                      class="relative z-10 flex items-center justify-center w-20 h-20"
                    >
                      <ColorImg
                        :src="
                          selectedTab == 'orders'
                            ? '/images/icons/motorbike.svg'
                            : selectedTab == 'ordersDone'
                              ? '/images/icons/package-delivered.svg'
                              : '/images/icons/pie-chart.svg'
                        "
                        class="p-4 bg-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <p class="text-2xl font-semibold text-gray-800">
                      <span>
                        {{
                          selectedTab == 'orders'
                            ? 'Aucune course disponible'
                            : selectedTab == 'ordersDone'
                              ? 'Aucune course terminée'
                              : 'Aucune commission disponible'
                        }}
                      </span>
                    </p>
                    <p class="text-gray-500 text-base mt-2">
                      <span>
                        {{
                          selectedTab == 'orders'
                            ? 'Les courses sont affectées aux autres livreurs'
                            : selectedTab == 'ordersDone'
                              ? "Vous n'avez pas encore terminé de courses"
                              : "Aucune course n'a été terminée"
                        }}
                      </span>
                    </p>
                  </div>
                </div>

                <div v-else>
                  <template v-for="(item, i) in allItems" :key="i">
                    <div class="mb-5">
                      <div class="border-b">
                        <!-- Vue "Commandes" (Actives ou Terminées) -->
                        <div
                          v-if="
                            selectedTab === 'orders' ||
                            selectedTab === 'ordersDone'
                          "
                          @click="showItem(item, selectedTab)"
                          class="grid grid-cols-4 gap-12 pb-4 p-2 ripple rounded-lg"
                        >
                          <div class="col-span-3">
                            <!-- Titre dynamique selon le type d'item -->
                            <div class="text-lg title-color font-bold truncate">
                              <template v-if="item.type === 'order'">
                                {{
                                  item?.restaurantName && item?.customerName
                                    ? `Food à ${item?.customerName} depuis ${item?.restaurantName}`
                                    : item?.customerName ||
                                      item?.restaurantName ||
                                      packageTypes[item?.packageType] ||
                                      'Livraison'
                                }}
                              </template>
                              <template v-else>
                                {{ 'Coli pour ' + item?.customerName }}
                              </template>
                            </div>

                            <!-- Description du colis -->
                            <div
                              v-if="item?.packageDescription"
                              class="text-sm text-gray-500 line-clamp-1 mb-1"
                            >
                              {{ item.packageDescription }}
                            </div>

                            <!-- Horaires selon l'onglet -->
                            <div
                              v-if="selectedTab === 'ordersDone'"
                              class="text-lg title-color"
                            >
                              <div class="flex gap-2">
                                <div>
                                  <ColorImg
                                    src="/images/icons/calendar.png"
                                    class="bg-current w-6 h-6"
                                  />
                                </div>
                                <div class="first-letter:uppercase">
                                  {{
                                    Commons.time(
                                      item.assignedAt,
                                      'dddd D MMM YYYY',
                                    )
                                  }}
                                </div>
                              </div>
                              <div class="flex gap-2">
                                <div>
                                  <ColorImg
                                    src="/images/icons/timer.png"
                                    class="bg-current w-6 h-6"
                                  />
                                </div>
                                <div class="first-letter:uppercase">
                                  {{ Commons.time(item.assignedAt, 'HH:mm') }} à
                                  {{ Commons.time(item.deliveredAt, 'HH:mm') }}
                                </div>
                              </div>
                            </div>
                            <div v-else class="text-lg title-color">
                              <div class="flex gap-2">
                                <div>
                                  <ColorImg
                                    src="/images/icons/calendar.png"
                                    class="bg-current w-6 h-6"
                                  />
                                </div>
                                <div class="first-letter:uppercase">
                                  {{
                                    Commons.time(
                                      item.orderDate,
                                      'dddd D MMM YYYY, HH:mm',
                                    )
                                  }}
                                </div>
                              </div>
                            </div>

                            <!-- Points de livraison -->
                            <RoutePointsItems noLineThrough :delivery="item" />
                          </div>

                          <div
                            class="col-span-1 flex flex-col items-end gap-1 text-center"
                          ></div>
                        </div>

                        <!-- Vue "Autre" (Historique / Gains) -->
                        <div
                          v-else
                          class="flex justify-between gap-12 pb-4 p-2"
                        >
                          <div class="text-lg title-color">
                            <div class="text-lg title-color font-bold">
                              {{
                                packageTypes[item?.packageType] ||
                                item?.packageType
                              }}
                            </div>
                            <div class="flex gap-2">
                              <div>
                                <ColorImg
                                  src="/images/icons/calendar.png"
                                  class="bg-current w-6 h-6"
                                />
                              </div>
                              <div class="first-letter:uppercase">
                                {{
                                  Commons.time(
                                    item.assignedAt,
                                    'dddd D MMM YYYY',
                                  )
                                }}
                              </div>
                            </div>
                            <div class="flex gap-2">
                              <div>
                                <ColorImg
                                  src="/images/icons/timer.png"
                                  class="bg-current w-6 h-6"
                                />
                              </div>
                              <div class="first-letter:uppercase">
                                {{ Commons.time(item.assignedAt, 'HH:mm') }} à
                                {{ Commons.time(item.deliveredAt, 'HH:mm') }}
                              </div>
                            </div>
                          </div>
                          <div
                            class="flex flex-col justify-center items-center gap-2 text-center"
                          >
                            <button
                              class="!py-1 buttonComponent small flex items-center justify-center"
                            >
                              <div class="whitespace-nowrap">Reçu</div>
                            </button>
                            <div class="text-center">
                              <div class="whitespace-nowrap title-color">
                                {{
                                  Commons.formatNumber(
                                    item.riderCommission || 0,
                                    {
                                      suffix: ' FCFA',
                                    },
                                  )
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                </div>
              </div>
            </div>
          </template>
        </DataFetcher>
      </div>
    </div>
  </div>
</template>

<script setup>
import ListFetcher from '@/features/data/presentation/components/ListFetcher.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import { ref, computed, watch } from 'vue'
import RoutePointsItems from './RoutePointsItems.vue'
import packageTypes from '@/project/data/raws/packageTypes.json'
import Commons from '@/shared/utils/commons'
import ColorImg from '@/presentation/components/img/ColorImg.vue'

const props = defineProps({
  showItem: { type: Function, default: null },
  expanded: { type: Boolean, default: false },
  activeItem: {
    type: Object,
    default() {
      return {}
    },
  },
  isAvailable: { type: Boolean, default: true },
  isSuspended: { type: Boolean, default: false },
})

const selectedTab = ref('orders')

// KYC state
const kycStatus = ref('not_filled')
const kycStatusData = ref(null)
const packagesEmpty = ref(false)
const ordersEmpty = ref(false)
const packagesList = ref([])
const ordersList = ref([])

const allItems = computed(() => {
  let dateKey = 'createdAt'
  const sortedByDate = [...packagesList.value, ...ordersList.value].sort(
    (a, b) => new Date(b[dateKey]) - new Date(a[dateKey]),
  )
  if (selectedTab.value == 'orders') sortedByDate.reverse()
  return sortedByDate
})

// Computed: check if KYC is validated
const kycValidated = computed(() => {
  return kycStatus.value === 'validated'
})

// Handle KYC data fetch
function onKycFetched({ data }) {
  console.log('ItemsSection - KYC status:', data)
  if (data && data.status) {
    kycStatus.value = data.status
    kycStatusData.value = data.statusData
  } else {
    kycStatus.value = 'error'
  }
}
watch(selectedTab, newVal => {
  ordersEmpty.value = false
  packagesEmpty.value = false
  ordersList.value = []
  packagesList.value = []
})
</script>
